import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { crmApi } from "../../lib/crm-api";
import { queryKeys } from "../../lib/queryKeys";
import type { CompanyDetail, CompanyWrite } from "../../lib/crm-types";
import { cn } from "../ui/cn";

/**
 * This account's own at-risk rules, and whether it is a key account.
 *
 * ## Why an account manager gets to override the desk
 *
 * The brief asks for it, and the reason is the whole difficulty with a lapsing
 * list: a builders' merchant ordering weekly is in trouble after three silent
 * weeks, and a specialist who orders twice a year is not. One threshold across
 * a book flags the second constantly, and a list that is mostly false alarms is
 * a list nobody opens. So the desk sets a sensible default and the person who
 * actually knows the customer can say otherwise.
 *
 * Blank is the normal state and means "use the team's rule", which is why the
 * inherited number is the placeholder rather than the value: a manager raising
 * the team rule from 30 to 45 should move every account that has not been
 * deliberately pinned, and pre-filling every box with 30 would quietly pin all
 * of them.
 *
 * The decline percentage is deliberately not here. What counts as a worrying
 * drop in spend is a desk-wide judgement, and a per-account dial would be a way
 * to silence an awkward account rather than deal with it.
 */
export function AtRiskPanel({ company }: { company: CompanyDetail }) {
  const queryClient = useQueryClient();

  /* The team defaults, so each box can show what it would inherit. Shared key
     with the settings panel, so changing a rule there updates this. */
  const settings = useQuery({
    queryKey: queryKeys.crmAmSettings,
    queryFn: crmApi.amSettings,
    staleTime: 5 * 60_000,
  });

  const save = useMutation({
    mutationFn: (patch: Partial<CompanyWrite>) =>
      crmApi.updateCompany(company.id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.crmCompany(company.id),
      });
      /* The dashboard's flags and ranking are computed from these, so its
         cached figures are wrong the moment one changes. */
      void queryClient.invalidateQueries({ queryKey: ["crm", "am-dashboard"] });
      void queryClient.invalidateQueries({ queryKey: queryKeys.crmCompaniesRoot });
    },
  });

  /*
   * Which rules this account would inherit.
   *
   * Best-effort: the exact team is resolved server-side from the owner's
   * membership, and reproducing that here would be a second implementation to
   * disagree with the first. Where the desks differ, the placeholder shows the
   * stricter of them -- an inherited number shown a little low is a hint, and
   * this is a hint rather than a statement of what will happen.
   */
  const inherited = settings.data?.settings ?? [];
  const noOrder = Math.min(...inherited.map((s) => s.noOrderDays), 30);
  const noContact = Math.min(...inherited.map((s) => s.noContactDays), 30);

  return (
    <section className="rounded-lg border border-line">
      <header className="border-b border-line px-3 py-2">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-faint">
          Attention rules
        </h2>
      </header>

      <div className="space-y-3 p-3">
        <label className="flex items-start gap-2 text-xs text-content">
          <input
            type="checkbox"
            checked={company.isKeyAccount}
            disabled={save.isPending}
            onChange={(event) =>
              save.mutate({ isKeyAccount: event.target.checked })
            }
            className="mt-0.5 size-3.5 shrink-0 accent-brand-600"
          />
          <span>
            Key account
            <span className="block text-[11px] text-faint">
              Shows on the Key customers tab of the dashboard, for everybody
            </span>
          </span>
        </label>

        <Override
          label="Chase after no order for"
          value={company.atRiskNoOrderDays}
          inherited={noOrder}
          disabled={save.isPending}
          onSave={(value) => save.mutate({ atRiskNoOrderDays: value })}
        />

        <Override
          label="Chase after no contact for"
          value={company.atRiskNoContactDays}
          inherited={noContact}
          disabled={save.isPending}
          onSave={(value) => save.mutate({ atRiskNoContactDays: value })}
          title="Counts real contact only -- a note, a call, an email, a meeting, a quote or an order. Changing the account manager or a field on this record does not reset it."
        />

        {save.isError ? (
          <p className="text-[11px] text-sla-breached">
            Could not save that. Try again.
          </p>
        ) : null}
      </div>
    </section>
  );
}

function Override({
  label,
  value,
  inherited,
  disabled,
  onSave,
  title,
}: {
  label: string;
  /** Null means inheriting, which is drawn as an empty box. */
  value: number | null;
  inherited: number;
  disabled?: boolean;
  onSave: (value: number | null) => void;
  title?: string;
}) {
  return (
    <label className="block text-xs text-content" title={title}>
      <span className="block">{label}</span>
      <span className="mt-1 flex items-center gap-1.5">
        <input
          type="number"
          min={1}
          max={730}
          /* Uncontrolled, keyed on the stored value so the box re-seeds when
             the query lands and typing is never fought by a refetch. */
          key={String(value)}
          defaultValue={value ?? ""}
          disabled={disabled}
          placeholder={String(inherited)}
          onBlur={(event) => {
            const raw = event.target.value.trim();
            const next = raw === "" ? null : Number(raw);
            if (next !== null && (!Number.isFinite(next) || next < 1)) return;
            if (next !== value) onSave(next);
          }}
          className={cn(
            "h-7 w-16 rounded-md border border-line bg-surface px-2 text-xs text-content",
            "outline-none focus:border-brand-400",
          )}
        />
        <span className="text-[11px] text-faint">
          {value === null ? `days — using the desk rule` : "days"}
        </span>
      </span>
    </label>
  );
}
